import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AuthHeader from '../components/Login/AuthHeader';
import AuthForm from '../components/Login/AuthForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { SetData, SetToken } from '../auth/Auth';
import serverClient from '../api/ServerClient';
import Loader from '../components/Loader';
import { BACKEND_SERVER, NEW_USER_KEY } from '../config/app.config';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';

function Login() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t, language, fetchLanguageSettings } = useLanguage();
    const [loading, setLoading] = useState(false);

    const [emailAddress, setEmailAddress] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const IsInvalid = password === '' || emailAddress === '';

    useEffect(() => {
        function handleMessage(event) {
            if (event.origin !== BACKEND_SERVER) {
                return;
            }

            const { error } = event.data;
            if (error) {
                setError(error);
                return;
            }
            const { accessToken, name, isNewUser } = event.data;

            if (accessToken) {
                SetToken(accessToken, name, 3600);
                SetData(NEW_USER_KEY, isNewUser === 'true' || isNewUser === '1');

                // Fetch language settings from API after successful OAuth login
                fetchLanguageSettings(true).then(() => {
                    if (isNewUser === 'true' || isNewUser === '1') {
                        navigate('/new-user');
                    } else {
                        navigate('/');
                    }
                });
            }
        }

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [navigate, fetchLanguageSettings]);

    async function handleSubmit(event) {
        event.preventDefault();
        setLoading(true);
        const data = {
            email: emailAddress,
            password,
        };
        try {
            const response = await serverClient.post(`/api/auth/login`, data);
            const accessToken = response?.data?.accessToken;
            const name = response?.data?.username;
            const expiresAt = response?.data?.expiresAt || 3600;
            const isNewUser = response?.data?.is_new_user || false;
            setPassword('');
            setEmailAddress('');
            setError('');
            SetToken(accessToken, name, expiresAt);
            SetData(NEW_USER_KEY, isNewUser);

            // Fetch language settings from API after successful login
            await fetchLanguageSettings(true);

            if (isNewUser === 1) {
                navigate('/new-user');
            } else {
                navigate(state?.pathname || '/');
            }
        } catch (err) {
            if (!err?.response) {
                setError(t('checkNetwork'));
            } else if (err.response?.status === 400) {
                setError(t('invalidCredentials'));
            } else if (err.response?.status === 401) {
                setError(t('invalidCredentials'));
            } else if (err.response?.status === 404) {
                setError(t('invalidCredentials'));
            } else {
                setError(t('somethingWentWrong'));
            }
        }
        setLoading(false);
    }

    return (
        <>
            <Helmet>
                <title>{getPageMeta('login', language).title}</title>
                <meta name="description" content={getPageMeta('login', language).description} />
                <meta name="keywords" content={getPageMeta('login', language).keywords} />
                <link rel="canonical" href={getPageMeta('login', language).url} />

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('login', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('login', language).description}
                />
                <meta property="og:url" content={getPageMeta('login', language).url} />
                <meta property="og:image" content={getPageMeta('login', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('login', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('login', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('login', language).image} />
            </Helmet>
            <AuthHeader />
            <Loader loading={loading} />
            {/*{error && <Alert alert={"danger"}> {error} </Alert>}*/}
            <AuthForm
                email={emailAddress}
                setEmail={setEmailAddress}
                password={password}
                setPassword={setPassword}
                loading={loading}
                error={error}
                IsInvalid={IsInvalid}
                handleSubmit={handleSubmit}
            />
        </>
    );
}

export default Login;
